import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "../../ui/Modal";
import Button from "../../ui/Button";
import Notification from "../shared/Notification";
import LoginRequiredModal from "../shared/LoginRequiredModal";
import { formatCurrency } from "../../../lib/currency";

function formatTimeOnly(value) {
  if (!value) return "";
  const date =
    typeof value === "string" && value.length > 5 && !value.includes(" ")
      ? new Date(value)
      : null;
  if (date && !Number.isNaN(date.getTime())) {
    return `${date.getUTCHours()}:${date
      .getUTCMinutes()
      .toString()
      .padStart(2, "0")}`;
  }
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  return match ? match[0] : value;
}

const InquiryModal = ({
  isOpen,
  onClose,
  isArtistLoggedIn,
  inquiry,
  artistServices,
}) => {
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  const serviceOptions = useMemo(
    () =>
      (artistServices || []).map((service) => ({
        value: String(service.id),
        label: service.name,
      })),
    [artistServices],
  );

  const hasArtistServices = serviceOptions.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setSelectedServiceId("");
    }
  }, [isOpen, setSelectedServiceId]);

  const resetNotification = () => setNotification({ message: "", type: "" });

  const notifyServiceError = (key = "service_required") => {
    setNotification({
      message: I18n.t(`recycled_inquiries.index.inquiry_modal.${key}`),
      type: "error",
    });
  };

  const copyToClipboard = async (url) => {
    try {
      // eslint-disable-next-line no-undef
      if (navigator.clipboard && navigator.clipboard.writeText) {
        // eslint-disable-next-line no-undef
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setNotification({
        message: I18n.t("recycled_inquiries.index.inquiry_modal.copied"),
        type: "success",
      });
      setTimeout(() => {
        resetNotification();
      }, 2000);
    } catch (err) {
      // Silently fail if copy doesn't work
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Failed to copy URL:", err);
      }
    }
  };

  const handleShare = async () => {
    const currentUrl = window.location.href;
    const shareData = {
      title: document.title,
      url: currentUrl,
    };

    try {
      // Check if Web Share API is available
      // eslint-disable-next-line no-undef
      if (navigator.share) {
        // Check if the URL can be shared
        // eslint-disable-next-line no-undef
        if (navigator.canShare && !navigator.canShare(shareData)) {
          // If canShare exists and returns false, fall back to copy
          await copyToClipboard(currentUrl);
          return;
        }

        try {
          // eslint-disable-next-line no-undef
          await navigator.share(shareData);
          // No notification needed - user is aware they shared successfully
          return;
        } catch (shareError) {
          // User cancelled or share failed, fall back to copy
          if (shareError.name !== "AbortError") {
            // Only show fallback message if it wasn't user cancellation
            await copyToClipboard(currentUrl);
            setNotification({
              message: I18n.t(
                "recycled_inquiries.index.inquiry_modal.share_failed",
              ),
              type: "info",
            });
            setTimeout(() => {
              resetNotification();
            }, 3000);
            return;
          }
          // User cancelled, do nothing
          return;
        }
      } else {
        // Web Share API not available, fall back to copy
        await copyToClipboard(currentUrl);
      }
    } catch (err) {
      // Final fallback to copy if everything fails
      await copyToClipboard(currentUrl);
    }
  };

  const handleKnownError = (errorCode) => {
    if (!errorCode) {
      return false;
    }

    if (errorCode === "already_interested") {
      setNotification({
        message: I18n.t(
          "recycled_inquiries.index.inquiry_modal.already_interested",
        ),
        type: "warning",
      });
      return true;
    }

    if (errorCode === "artistic_service_required") {
      notifyServiceError();
      return true;
    }

    if (errorCode === "artistic_service_invalid") {
      notifyServiceError("service_invalid");
      return true;
    }

    if (errorCode === "forbidden") {
      setNotification({
        message: I18n.t("recycled_inquiries.index.inquiry_modal.not_logged_in"),
        type: "error",
      });
      return true;
    }

    if (errorCode === "not_found") {
      setNotification({
        message: I18n.t("recycled_inquiries.index.inquiry_modal.error"),
        type: "error",
      });
      return true;
    }

    return false;
  };

  if (!isOpen) return null;

  const handleInterested = async () => {
    if (!inquiry) return;

    if (!hasArtistServices) {
      notifyServiceError("no_services_error");
      return;
    }

    if (!selectedServiceId) {
      notifyServiceError();
      return;
    }

    setLoading(true);
    resetNotification();
    try {
      $.ajax({
        url: "/recycled_inquiries/artist_interests",
        type: "POST",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          recycled_inquiry_id: inquiry.id,
          artistic_service_id: Number(selectedServiceId),
        }),
        success: (data, _textStatus, jqXHR) => {
          if (jqXHR.status === 201 || data.success) {
            setNotification({
              message: I18n.t("recycled_inquiries.index.inquiry_modal.success"),
              type: "success",
            });
            setTimeout(() => {
              resetNotification();
              onClose();
            }, 2400);
          } else if (!handleKnownError(data.error)) {
            setNotification({
              message: I18n.t("recycled_inquiries.index.inquiry_modal.error"),
              type: "error",
            });
          }
        },
        error: (jqXHR) => {
          let data = {};
          try {
            data = jqXHR.responseJSON || {};
          } catch (e) {
            /* ignore */
          }
          if (!handleKnownError(data.error)) {
            setNotification({
              message: I18n.t("recycled_inquiries.index.inquiry_modal.error"),
              type: "error",
            });
          }
        },
        complete: () => {
          setLoading(false);
          setTimeout(() => {
            resetNotification();
          }, 3600);
        },
      });
    } catch (e) {
      setNotification({
        message: I18n.t("recycled_inquiries.index.inquiry_modal.error"),
        type: "error",
      });
      setLoading(false);
    }
  };

  const submitDisabled = loading || !hasArtistServices || !selectedServiceId;

  if (isArtistLoggedIn && inquiry) {
    const {
      currency,
      date,
      occasion_date: occasionDate,
      city,
      service_type,
      kind_of_moment,
      budget_min,
      budget_max,
      service_sub_types,
    } = inquiry;
    const eventDate = occasionDate || date;
    const currentLocale = I18n.locale;
    const subcategoryTranslation = I18n.t(`subtypes.${service_type}`);
    const kindOfMomentTranslation = I18n.t(`kinds_of_moment.${kind_of_moment}`);
    let budget = null;
    if (budget_min && budget_max) {
      budget = `${formatCurrency(
        currentLocale,
        currency,
        budget_min,
      )} - ${formatCurrency(currentLocale, currency, budget_max)}`;
    } else if (budget_min) {
      budget = `${formatCurrency(currentLocale, currency, budget_min)}+`;
    } else if (budget_max) {
      budget = `0 - ${formatCurrency(currentLocale, currency, budget_max)}`;
    }
    return (
      <>
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={resetNotification}
        />
        <Modal isOpen={isOpen} onClose={onClose} extraClass="b-inquiry-modal">
          <div className="b-artist-inquiry">
            <h3
              className="b-artist-inquiry__title"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <span>
                {city
                  ? service_type
                    ? I18n.t("generic.jobs.title_with_city", {
                        service_type: subcategoryTranslation,
                        city,
                      })
                    : I18n.t("generic.jobs.title_with_city", {
                        service_type: kindOfMomentTranslation,
                        city,
                      })
                  : service_type
                    ? I18n.t("generic.jobs.title_without_city", {
                        service_type: subcategoryTranslation,
                      })
                    : I18n.t("generic.jobs.title_without_city", {
                        service_type: kindOfMomentTranslation,
                      })}
              </span>
              <button
                type="button"
                onClick={handleShare}
                className="b-artist-inquiry__copy"
                aria-label={I18n.t(
                  "recycled_inquiries.index.inquiry_modal.shared",
                )}
                title={I18n.t(
                  "recycled_inquiries.index.inquiry_modal.share_title",
                )}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </button>
            </h3>
            <dl className="b-artist-inquiry__list">
              <dt className="b-artist-inquiry__term">
                {I18n.t("inquiry_preview.where")}
              </dt>
              <dd className="b-artist-inquiry__data b-artist-inquiry__data--highlighted">
                {inquiry.city || ""}
              </dd>
              {!!inquiry.kind_of_art && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t(
                      "recycled_inquiries.index.inquiry_modal.kind_of_art",
                    )}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {I18n.t(`categories.${inquiry.kind_of_art}`, {
                      defaultValue: inquiry.kind_of_art,
                    })}
                  </dd>
                </>
              )}
              {!!service_type && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t(
                      "recycled_inquiries.index.inquiry_modal.service_type",
                    )}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {service_type ? I18n.t(`subtypes.${service_type}`) : "-"}
                  </dd>
                </>
              )}
              {!!service_sub_types && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t(
                      "recycled_inquiries.index.inquiry_modal.subcategory",
                    )}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {typeof service_sub_types === "object" &&
                    service_sub_types.length > 0 ? (
                      <ul className="b-artist-inquiry__data-list">
                        {service_sub_types.map((subtype) => (
                          <li key={subtype}>{I18n.t(`subtypes.${subtype}`)}</li>
                        ))}
                      </ul>
                    ) : (
                      "-"
                    )}
                  </dd>
                </>
              )}
              {!!kind_of_moment && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("recycled_inquiries.index.inquiry_modal.occasion")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {kind_of_moment
                      ? I18n.t(`kinds_of_moment.${kind_of_moment}`)
                      : "-"}
                  </dd>
                </>
              )}
              {!!eventDate && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.date_of_event")}
                  </dt>
                  <dd className="b-artist-inquiry__data b-artist-inquiry__data--highlighted">
                    {I18n.l("date.formats.long", eventDate)}
                  </dd>
                </>
              )}
              {!!inquiry.occasion_time && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.desired_starting_time")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {formatTimeOnly(inquiry.occasion_time)}
                  </dd>
                </>
              )}
              {!!inquiry.show_beginning && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.desired_starting_time")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {formatTimeOnly(inquiry.show_beginning)}
                  </dd>
                </>
              )}
              {!!inquiry.show_duration && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.desired_duration")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {inquiry.show_duration}
                  </dd>
                </>
              )}
              {!!inquiry.guests_count && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.guests_count")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {inquiry.guests_count}
                  </dd>
                </>
              )}
              {!!inquiry.environment && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.environment")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {inquiry.environment}
                  </dd>
                </>
              )}
              <dt className="b-artist-inquiry__term">
                {I18n.t("inquiry_preview.estimated_budget")}
              </dt>
              <dd className="b-artist-inquiry__data">
                {budget !== null
                  ? budget
                  : I18n.t("inquiry_preview.not_provided")}
              </dd>
              {!!inquiry.description && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.description")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {inquiry.description}
                  </dd>
                </>
              )}
              {!!inquiry.additional_description && (
                <>
                  <dt className="b-artist-inquiry__term">
                    {I18n.t("inquiry_preview.additional_booking_details")}
                  </dt>
                  <dd className="b-artist-inquiry__data">
                    {inquiry.additional_description ||
                      I18n.t("inquiry_preview.not_provided")}
                  </dd>
                </>
              )}
            </dl>
            <div className="b-job-board__modal-cta">
              <div style={{ width: "100%", marginBottom: 16 }}>
                {hasArtistServices ? (
                  <>
                    <label
                      htmlFor="artist-service-select"
                      className="b-artist-inquiry__term"
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      {I18n.t(
                        "recycled_inquiries.index.inquiry_modal.select_service_label",
                      )}
                    </label>
                    <select
                      id="artist-service-select"
                      value={selectedServiceId}
                      onChange={(event) =>
                        setSelectedServiceId(event.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: 4,
                        border: "1px solid #ddd",
                        fontSize: 14,
                      }}
                      disabled={loading}
                    >
                      <option value="">
                        {I18n.t(
                          "recycled_inquiries.index.inquiry_modal.select_service_placeholder",
                        )}
                      </option>
                      {serviceOptions.map((service) => (
                        <option key={service.value} value={service.value}>
                          {service.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <div
                    className="b-job-board__modal-service-empty"
                    style={{
                      backgroundColor: "#fff6e5",
                      borderRadius: 4,
                      padding: "12px 16px",
                      fontSize: 14,
                      color: "#6a3b00",
                      textAlign: "left",
                    }}
                  >
                    {I18n.t(
                      "recycled_inquiries.index.inquiry_modal.no_services_body",
                    )}
                  </div>
                )}
              </div>
              <Button
                primary
                onClick={handleInterested}
                disabled={submitDisabled}
              >
                {loading
                  ? I18n.t("recycled_inquiries.index.inquiry_modal.loading")
                  : I18n.t(
                      "recycled_inquiries.index.inquiry_modal.im_interested",
                    )}
              </Button>
              <div className="b-job-board__modal-cta-text">
                {I18n.t(
                  "recycled_inquiries.index.inquiry_modal.im_interested_info",
                )}
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return <LoginRequiredModal isOpen={isOpen} onClose={onClose} />;
};

InquiryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isArtistLoggedIn: PropTypes.bool.isRequired,
  inquiry: PropTypes.object,
  artistServices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      profileId: PropTypes.number,
      profileName: PropTypes.string,
    }),
  ),
};

InquiryModal.defaultProps = {
  inquiry: null,
  artistServices: [],
};

export default InquiryModal;
